import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import type { ConversationShare, ShareRole, Visibility } from "@hector/shared-types/resource-sharing";

/**
 * Hooks de partage nominatif d'une conversation (resource-sharing Phase 4).
 *
 * Miroir strict de {@link import('./useCaseShares')} (même contrat owner-only,
 * re-gardé côté serveur — l'UI ne fait que masquer) mais sur les endpoints
 * conversation :
 * - `GET    /conversations/:id/shares`     → `ConversationShare[]`
 * - `POST   /conversations/:id/shares`     → body `{ targetUserId, role }`
 * - `DELETE /conversations/:id/shares/:userId`
 * - `PATCH  /conversations/:id/visibility` → body `{ visibility }`
 *
 * Les mutations invalident la clé spécifique `['conversation-shares', id]` (la
 * modale) ET les clés de liste `['conversations', 'global']` + `['conversations']` :
 * un partage/retrait ou un changement de visibilité modifie `my_role`/`shares`/
 * `visibility` des DTO renvoyés par `GET /conversations/global`, donc la sidebar
 * (section « Partagées avec moi », badges, bouton Partager) doit se rafraîchir.
 *
 * Le helper de sélection de rôle (`NO_ACCESS`, `roleForUser`, `ShareRoleSelection`)
 * est réutilisé depuis `useCaseShares` : il est structurellement générique (une
 * `ConversationShare` a la même forme qu'une `CaseShare`), pas la peine de le
 * dupliquer.
 */

/** Liste des partages d'une conversation (owner-only). */
export function useConversationShares(conversationId: string | null | undefined) {
  return useQuery<ConversationShare[]>({
    queryKey: ["conversation-shares", conversationId],
    queryFn: async () => {
      if (!conversationId) return [];
      try {
        // Le backend renvoie `{ data: { visibility, shares } }` ; apiClient déballe `data`
        // → on reçoit `{ visibility, shares }`, PAS un tableau. Extraire `.shares` (sinon
        // roleForUser fait `.find` sur un objet → TypeError → crash de la modale).
        const res = await apiClient.get<{ visibility: string | null; shares: ConversationShare[] }>(
          `/conversations/${conversationId}/shares`,
        );
        return res?.shares ?? [];
      } catch (error) {
        // 403 (non-owner) improbable — la modale n'est ouverte que par l'owner —
        // mais on reste défensif : log + PostHog, pas de toast (état vide dégradé).
        ErrorService.handle(error, {
          context: {
            action: "fetch_conversation_shares",
            conversation_id: conversationId ?? undefined,
          },
          silent: true,
        });
        return [];
      }
    },
    enabled: !!conversationId,
  });
}

/**
 * Invalidation commune aux mutations de partage/visibilité : la modale + la liste
 * globale de conversations (clé spécifique `['conversations','global']` ET
 * générale `['conversations']`, cf. convention repo).
 */
function useInvalidateConversationShareViews(conversationId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["conversation-shares", conversationId] });
    queryClient.invalidateQueries({ queryKey: ["conversations", "global"] });
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
  };
}

/** Accorde (ou met à jour) un partage — `POST /conversations/:id/shares`. */
export function useAddConversationShare(conversationId: string) {
  const invalidate = useInvalidateConversationShareViews(conversationId);

  return useMutation({
    mutationFn: async (payload: { targetUserId: string; role: ShareRole }) => {
      return await apiClient.post(`/conversations/${conversationId}/shares`, payload);
    },
    onSuccess: invalidate,
    onError: (error) => {
      // 400 = destinataire hors cabinet (improbable, la liste vient du même cabinet),
      // 403 = appelant non-owner (défensif). Toast neutre via ErrorService.
      ErrorService.handle(error, {
        context: { action: "add_conversation_share", conversation_id: conversationId },
      });
    },
  });
}

/** Retire un partage — `DELETE /conversations/:id/shares/:userId`. */
export function useRemoveConversationShare(conversationId: string) {
  const invalidate = useInvalidateConversationShareViews(conversationId);

  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/conversations/${conversationId}/shares/${userId}`);
      return { success: true };
    },
    onSuccess: invalidate,
    onError: (error) => {
      ErrorService.handle(error, {
        context: { action: "remove_conversation_share", conversation_id: conversationId },
      });
    },
  });
}

/** Bascule la visibilité de la conversation — `PATCH /conversations/:id/visibility`. */
export function useUpdateConversationVisibility(conversationId: string) {
  const invalidate = useInvalidateConversationShareViews(conversationId);

  return useMutation({
    mutationFn: async (visibility: Visibility) => {
      return await apiClient.patch(`/conversations/${conversationId}/visibility`, {
        visibility,
      });
    },
    onSuccess: invalidate,
    onError: (error) => {
      ErrorService.handle(error, {
        context: { action: "update_conversation_visibility", conversation_id: conversationId },
      });
    },
  });
}
